/* eslint-disable import/no-extraneous-dependencies */
import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { AgGridReact } from 'ag-grid-react';
import moment from 'moment';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';

import { H2, Text, Input, Icon } from '../../../components';
import { useQuestionReview } from '../providers/QuestionReviewProvider';
import { useUsers } from '../providers';
import { QuestionLinkRenderer, ExternalLinkRenderer } from '../components/atoms';

const QuestionReviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  row-gap: 8px;
  border-bottom: 1px solid var(--border-muted);
`;

const TableContainer = styled.div`
  padding: 20px;
  height: 700px;
  --ag-font-size: 13px;
  --ag-font-family: var(--font-family-body);
  --ag-header-background-color: var(--wash-gray-default);
  --ag-header-foreground-color: var(--content-title);
  --ag-foreground-color: var(--content-body);
  --ag-background-color: var(--white);
  --ag-header-height: 40px;
  --ag-row-height: 40px;
  --ag-border-radius: 8px;
  --ag-row-hover-color: var(--elevation-mid);

  .ag-header-cell {
    font-weight: 600;
  }

  /* Make the grid container relative and adjust the root wrapper */
  .ag-root {
    position: relative;
    height: 100%;
  }

  .ag-root-wrapper {
    height: calc(100% - 40px) !important; /* Leave space for pagination */
    border-bottom: none !important; /* Remove bottom border as pagination will have it */
  }
`;

const AgGridPaginationPanel = styled.div`
  /* Position within the AG Grid root */
  position: absolute;
  bottom: 0;
  left: 1px; /* Account for grid border */
  right: 1px; /* Account for grid border */
  height: 40px;
  background: var(--ag-background-color);
  border: 1px solid var(--ag-border-color);
  border-top: none; /* Connect seamlessly with grid */
  border-bottom-left-radius: var(--ag-border-radius);
  border-bottom-right-radius: var(--ag-border-radius);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  font-family: var(--ag-font-family);
  font-size: var(--ag-font-size);
  color: var(--ag-foreground-color);
  box-sizing: border-box;

  /* Ensure it appears above grid content */
  z-index: 10;
`;

const AgGridPaginationSummary = styled.div`
  color: var(--ag-foreground-color);
  font-size: var(--ag-font-size);
  white-space: nowrap;
`;

const AgGridPaginationButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const AgGridPaginationButton = styled.button`
  width: 20px;
  height: 20px;
  border: 1px solid var(--ag-border-color);
  background: ${(props) => (props.disabled ? 'transparent' : 'var(--ag-background-color)')};
  color: ${(props) =>
    props.disabled ? 'var(--ag-disabled-foreground-color)' : 'var(--ag-foreground-color)'};
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-family: var(--ag-font-family);
  border-radius: 1px;

  &:hover:not(:disabled) {
    background: var(--ag-row-hover-color);
    border-color: var(--ag-border-color);
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }

  /* AG Grid-style arrow icons */
  &.ag-icon-previous::before {
    content: '‹';
    font-size: 14px;
    font-weight: bold;
  }

  &.ag-icon-next::before {
    content: '›';
    font-size: 14px;
    font-weight: bold;
  }
`;

const AgGridPageInfo = styled.span`
  margin: 0 6px;
  font-size: var(--ag-font-size);
  color: var(--ag-foreground-color);
  white-space: nowrap;
`;

const SearchContainer = styled.div`
  position: relative;
  padding: 20px 20px 0 20px;
`;

const SearchInput = styled(Input)`
  height: 40px;
  background: var(--wash-gray-default);
  padding: 4px 38px;
  border-radius: 10px;
  box-sizing: border-box;
  max-width: 400px;
`;

const SearchIcon = styled(Icon)`
  position: absolute;
  top: 30px;
  left: 29px;
`;

const QuestionReview = () => {
  const {
    questions,
    hasNextPage,
    isLoading,
    handlePaginationChanged,
    currentPage,
    handleSearch,
    searchQuery
  } = useQuestionReview();
  const { users } = useUsers();
  const [inputValue, setInputValue] = useState(searchQuery || '');

  const executeSearch = useCallback(() => {
    handleSearch(inputValue);
  }, [inputValue, handleSearch]);

  const handleInputChange = useCallback((e) => {
    const { value } = e.target;
    setInputValue(value);
  }, []);

  const handleKeyPress = useCallback(
    (e) => {
      // Trigger search on Enter, Space, or Dot
      if (e.key === 'Enter' || e.key === ' ' || e.key === '.') {
        e.preventDefault();
        executeSearch();
      }
    },
    [executeSearch]
  );

  const getUserName = (userId) => {
    if (!userId) return '';
    const user = users.find((u) => u.id === userId);
    return user?.name || '';
  };

  const pageSize = 15;
  const startRecord = (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, startRecord + questions.length - 1);

  let totalRecords;
  if (hasNextPage) {
    totalRecords = `more than ${endRecord.toLocaleString()}`;
  } else {
    totalRecords = questions.length.toLocaleString();
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      handlePaginationChanged(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      handlePaginationChanged(currentPage + 1);
    }
  };

  const columnDefs = [
    {
      headerName: 'Question',
      field: 'body',
      flex: 2,
      minWidth: 200,
      resizable: false,
      cellRenderer: QuestionLinkRenderer
    },
    {
      headerName: 'Owner',
      field: 'userId',
      flex: 1,
      minWidth: 120,
      resizable: false,
      cellRenderer: (params) => getUserName(params.value)
    },
    {
      headerName: 'Source',
      field: 'schemas',
      flex: 1,
      minWidth: 120,
      resizable: false,
      cellRenderer: (params) => params.value?.join(', ') || 'N/A'
    },
    {
      headerName: 'Date Created',
      field: 'createdAt',
      flex: 1,
      minWidth: 120,
      resizable: false,
      cellRenderer: (params) => {
        if (!params.value) return 'N/A';
        return moment(params.value).format('MMMM D, YYYY [at] h:mm a');
      }
    },
    {
      headerName: '',
      field: 'external-link',
      width: 50,
      resizable: false,
      sortable: false,
      cellRenderer: ExternalLinkRenderer
    }
  ];

  return (
    <>
      <QuestionReviewContainer>
        <H2>Question Review</H2>
        <Text $color='var(--content-subtitle)'>
          Review questions and provide feedback to improve accuracy across your organization.
        </Text>
      </QuestionReviewContainer>
      <SearchContainer>
        <SearchIcon name='search' displaySize={20} color='var(--content-subtitle)' />
        <SearchInput
          placeholder='Search questions...'
          value={inputValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
        />
      </SearchContainer>
      <TableContainer className='ag-theme-quartz'>
        <div style={{ position: 'relative', height: '100%' }}>
          <AgGridReact
            rowData={questions}
            columnDefs={columnDefs}
            pagination={false}
            domLayout='normal'
            loading={isLoading}
            suppressPaginationPanel
            loadingOverlayComponent='agLoadingOverlay'
            overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading...</span>'
            overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">Loading...</span>'
          />

          {/* Custom pagination couldn't get it to work with native pagination */}
          <AgGridPaginationPanel>
            <AgGridPaginationSummary>
              {startRecord.toLocaleString()} to {endRecord.toLocaleString()} of {totalRecords}
            </AgGridPaginationSummary>
            <AgGridPaginationButtons>
              <AgGridPaginationButton
                className='ag-icon-previous'
                onClick={handlePreviousPage}
                disabled={currentPage <= 1 || isLoading}
                title='Previous Page'
              />
              <AgGridPageInfo>Page {currentPage.toLocaleString()}</AgGridPageInfo>
              <AgGridPaginationButton
                className='ag-icon-next'
                onClick={handleNextPage}
                disabled={!hasNextPage || isLoading}
                title='Next Page'
              />
            </AgGridPaginationButtons>
          </AgGridPaginationPanel>
        </div>
      </TableContainer>
    </>
  );
};

export { QuestionReview };
